import {
    Button, ContentfulBlogPostFields,
    ContentfulEntry,
    ContentfulHomepageSettings
} from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEntries } from '../lib/contentful';

interface CategoryCount {
  name: string;
  count: number;
}

const Home: React.FC = () => {
  const [homepageSettings, setHomepageSettings] = useState<ContentfulEntry<ContentfulHomepageSettings> | null>(null);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [recentArticles, setRecentArticles] = useState<ContentfulEntry<ContentfulBlogPostFields>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        // Fetch homepage settings
        const settingsEntries = await getEntries('homepageSettings');
        if (settingsEntries.length > 0) {
          setHomepageSettings(settingsEntries[0] as any);
        }

        // Fetch recent blog posts
        const blogEntries = await getEntries('blogPost');
        const sortedBlogs = (blogEntries as any[])
          .sort((a, b) => new Date(b.fields.publishDate).getTime() - new Date(a.fields.publishDate).getTime())
          .slice(0, 6);
        setRecentArticles(sortedBlogs);

        // Extract categories from blog posts
        const categoryMap = new Map<string, number>();
        (blogEntries as any[]).forEach(post => {
          const tags = post.fields.tags || [];
          tags.forEach((tag: string) => {
            categoryMap.set(tag, (categoryMap.get(tag) || 0) + 1);
          });
        });
        
        const extractedCategories = Array.from(categoryMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        setCategories(extractedCategories);

      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageData();
  }, []);

  // Fallback data
  const fallbackRecentArticles = [
    {
      sys: { id: '1', type: 'Entry', createdAt: '', updatedAt: '', contentType: { sys: { id: 'blogPost' } } },
      fields: {
        title: 'Decoupling Technical Architecture to Enhance Resiliency',
        publishDate: '2025-01-31',
        excerpt: "Let's explore the importance of decoupling, what to watch out for, metrics that measure its success, and how to communicate its value to stakeholders",
        slug: 'decoupling-technical-architecture-to-enhance-resiliency'
      }
    },
  ];

  const getIconSvg = (icon?: string) => {
    switch (icon) {
      case 'article':
        return (
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'project':
        return (
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        );
      case 'video':
        return (
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const heroSection = homepageSettings?.fields.heroSection?.fields;
  const ctaSection = homepageSettings?.fields.ctaSection?.fields;
  const exploreSection = homepageSettings?.fields.exploreSection?.fields;
  const resourceCards = homepageSettings?.fields.resourceCards || [];
  const displayArticles = recentArticles.length > 0 ? recentArticles : fallbackRecentArticles;

  return (
    <>
      {/* Hero Section with Video Background - Full viewport width */}
      <section 
        className="relative overflow-hidden min-h-[600px]"
        style={{
          marginTop: '-3rem',
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw'
        }}
      >
        {/* Video Background */}
        {heroSection?.videoUrl && (
          <div className="absolute inset-0 w-full h-full">
            <iframe
              src={heroSection.videoUrl}
              className="absolute top-0 left-0 w-full h-full"
              style={{ 
                width: '100vw',
                height: '56.25vw', // 16:9 aspect ratio
                minHeight: '100%',
                minWidth: '177.77vh', // 16:9 aspect ratio
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
              frameBorder="0"
              allow="autoplay; fullscreen"
              title="hero-background-video"
            />
          </div>
        )}
        
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Content */}
        <div className="relative z-10 text-center py-32 px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-4 drop-shadow-lg">
            {heroSection?.title || 'Building. Developing. Tinkering.'}
          </h1>
          <p className="text-xl text-white font-medium mb-8 drop-shadow-lg">
            {heroSection?.subtitle || 'MAKING NEW THINGS POSSIBLE!'}
          </p>
          <Link to={heroSection?.ctaLink || '/about'}>
            <Button variant="primary" size="lg" className="shadow-xl">
              {heroSection?.ctaText || 'Discover More'}
            </Button>
          </Link>
        </div>
      </section>

      <div className="space-y-16 mt-24">
        {/* Resource Catalog Section */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {homepageSettings?.fields.resourceCatalogTitle || 'Resource Catalog'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {homepageSettings?.fields.resourceCatalogDescription || 
                'Come and checkout our growing catalog of helpful tips and creative projects, curated, created and supplied direct to you.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {resourceCards.map((card) => (
              <Link key={card.sys.id} to={card.fields.link} className="group">
                <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 h-full">
                  <div className="text-gray-700 group-hover:text-green-600 transition-colors mb-4">
                    {getIconSvg(card.fields.icon)}
                  </div>
                  <h3 className="text-2xl font-semibold mb-3 text-gray-900 group-hover:text-green-600 transition-colors">
                    {card.fields.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {card.fields.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section with Parallax */}
        {ctaSection && (
          <section 
            className="relative text-white py-24"
            style={{
              width: '100vw',
              position: 'relative',
              left: '50%',
              right: '50%',
              marginLeft: '-50vw',
              marginRight: '-50vw',
              minHeight: '500px',
              overflow: 'hidden'
            }}
          >
            {/* Parallax Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: ctaSection.backgroundImage 
                  ? `url(${ctaSection.backgroundImage.fields.file.url})`
                  : 'url(/mountain-parallax.jpg)',
                backgroundAttachment: 'fixed',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                width: '100%',
                height: '100%'
              }}
            />
            
            {/* Grey transparent overlay for text readability */}
            <div className="absolute inset-0 bg-gray-900/40" />
            
            {/* Content */}
            <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8">
              <h2 className="text-4xl font-bold mb-6 drop-shadow-lg">{ctaSection.title}</h2>
              <p className="text-xl leading-relaxed mb-8 text-white drop-shadow-md">
                {ctaSection.description}
              </p>
              <Link to={ctaSection.ctaLink}>
                <Button 
                  variant="secondary" 
                  size="lg" 
                  className="!bg-white !text-green-600 hover:!bg-gray-100 !border-0 shadow-xl"
                >
                  {ctaSection.ctaText}
                </Button>
              </Link>
            </div>
          </section>
        )}

        {/* Recent Articles Section */}
        {homepageSettings?.fields.displayRecentArticles && displayArticles.length > 0 && (
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto">
              {displayArticles.map((article) => (
                <Link key={article.sys.id} to={`/blog/${article.fields.slug}`} className="group">
                  <article className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 flex flex-col h-full">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                      {article.fields.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {formatDate(article.fields.publishDate)}
                    </p>
                    <p className="text-gray-600 leading-relaxed flex-1">
                      {article.fields.excerpt || ''}
                    </p>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Categories and Explore Section */}
        <section className="bg-gray-50 p-8 rounded-lg border border-gray-200">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Categories */}
            {categories.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <Link 
                      key={category.name}
                      to={`/blog?category=${encodeURIComponent(category.name)}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all bg-white text-gray-700 border border-gray-200 hover:border-green-600 hover:text-green-600"
                    >
                      <span>{category.name}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        {category.count}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Explore Section */}
            {exploreSection && (
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{exploreSection.title}</h3>
                <p className="text-gray-600 mb-6">
                  {exploreSection.description}
                </p>
                <Link to={exploreSection.ctaLink}>
                  <Button variant="primary">{exploreSection.ctaText}</Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
